import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Grid,
  Divider,
  Skeleton,
} from "@mui/material";
import { PersonAdd, Email, Person, Refresh, Block, LockOpen, School, SchoolOutlined, Close } from "@mui/icons-material";
import {
  getMyEmployeesAPI,
  createInvitationAPI,
  createEmployeeWithUsernameAPI,
  updateEmployeeAPI,
  resendInvitationAPI,
  getEmployeeDetailsAPI,
} from "../../services/business";

export default function Employees() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", canRequestTutoring: true });
  const [createForm, setCreateForm] = useState({
    firstName: "",
    lastName: "",
    username: "",
    password: "",
    canRequestTutoring: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMyEmployeesAPI();
      setEmployees(res.data.employees || []);
      setInvitations(res.data.invitations || []);
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.msg || e.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async () => {
    if (!inviteForm.email?.trim()) {
      setSnack({ open: true, message: "Email is required", severity: "error" });
      return;
    }
    setSubmitting(true);
    try {
      await createInvitationAPI({ email: inviteForm.email.trim(), canRequestTutoring: inviteForm.canRequestTutoring });
      setSnack({ open: true, message: "Invitation sent", severity: "success" });
      setInviteOpen(false);
      setInviteForm({ email: "", canRequestTutoring: true });
      load();
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.msg || e.message, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async () => {
    const { firstName, lastName, username, password } = createForm;
    if (!firstName?.trim() || !lastName?.trim() || !username?.trim() || !password) {
      setSnack({ open: true, message: "All fields required", severity: "error" });
      return;
    }
    if (String(password).length < 8) {
      setSnack({ open: true, message: "Password at least 8 characters", severity: "error" });
      return;
    }
    setSubmitting(true);
    try {
      await createEmployeeWithUsernameAPI(createForm);
      setSnack({ open: true, message: "Account created", severity: "success" });
      setCreateOpen(false);
      setCreateForm({ firstName: "", lastName: "", username: "", password: "", canRequestTutoring: true });
      load();
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.msg || e.message, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleFreeze = async (studentId, currentStatus) => {
    try {
      const newStatus = currentStatus === "FROZEN" ? "ACTIVE" : "FROZEN";
      await updateEmployeeAPI(studentId, { accountStatus: newStatus });
      setSnack({ 
        open: true, 
        message: newStatus === "FROZEN" 
          ? (t("employees.employeeFrozen") || "Employee frozen") 
          : (t("employees.employeeUnfrozen") || "Employee unfrozen"), 
        severity: "success" 
      });
      load();
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.msg || e.message, severity: "error" });
    }
  };

  const handleToggleCanRequestTutoring = async (studentId, currentValue) => {
    try {
      await updateEmployeeAPI(studentId, { canRequestTutoring: !currentValue });
      setSnack({ 
        open: true, 
        message: !currentValue 
          ? (t("employees.canRequestEnabled") || "Can request tutoring enabled") 
          : (t("employees.canRequestDisabled") || "Can request tutoring disabled"), 
        severity: "success" 
      });
      load();
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.msg || e.message, severity: "error" });
    }
  };

  const handleRowClick = async (employee) => {
    setSelectedEmployee(employee);
    setDetailOpen(true);
    setLoadingDetails(true);
    try {
      const res = await getEmployeeDetailsAPI(employee._id);
      setEmployeeDetails(res.data);
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.msg || e.message, severity: "error" });
      setDetailOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleResend = async (invitationId) => {
    try {
      await resendInvitationAPI(invitationId);
      setSnack({ open: true, message: "Invitation resent", severity: "success" });
      load();
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.msg || e.message, severity: "error" });
    }
  };

  const name = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(" ") || u?.username || u?.email || "—";

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", p: 3, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("employees.title") || "Manage Employees"}
      </Typography>
      <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
        <Button startIcon={<Email />} variant="contained" onClick={() => setInviteOpen(true)}>
          {t("employees.inviteByEmail") || "Invite by email"}
        </Button>
        <Button startIcon={<PersonAdd />} variant="outlined" onClick={() => setCreateOpen(true)}>
          {t("employees.createWithUsername") || "Create account (no email)"}
        </Button>
      </Box>

      {loading ? (
        <Card sx={{ mb: 2 }}>
          <Box sx={{ p: 2, pb: 0 }}>
            <Skeleton variant="text" width={120} height={24} />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Skeleton variant="text" width={80} /></TableCell>
                <TableCell><Skeleton variant="text" width={120} /></TableCell>
                <TableCell><Skeleton variant="text" width={140} /></TableCell>
                <TableCell><Skeleton variant="text" width={80} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4].map((row) => (
                <TableRow key={row}>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={150} /></TableCell>
                  <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  <TableCell><Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                      <Skeleton variant="circular" width={32} height={32} />
                      <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <>
          <Card sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
              {t("employees.employees") || "Employees"}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("employees.tableName") || "Name"}</TableCell>
                  <TableCell>{t("employees.tableEmailUsername") || "Email/Username"}</TableCell>
                  <TableCell>{t("employees.tableCanRequestTutoring") || "Can request tutoring"}</TableCell>
                  <TableCell>{t("employees.tableStatus") || "Status"}</TableCell>
                  <TableCell align="right">{t("employees.tableActions") || "Actions"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t("employees.noEmployeesYet") || "No employees yet. Invite by email or create an account."}
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((e) => (
                    <TableRow 
                      key={e._id}
                      onClick={() => handleRowClick(e)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell>{name(e.userId)}</TableCell>
                      <TableCell>{e.userId?.email || e.userId?.username || "—"}</TableCell>
                      <TableCell>
                        {e.canRequestTutoring 
                          ? (t("common.yes") || "Yes") 
                          : (t("common.no") || "No")}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={e.accountStatus === "FROZEN" 
                            ? (t("employees.statusFrozen") || "Frozen") 
                            : (t("employees.statusActive") || "Active")} 
                          size="small" 
                          color={e.accountStatus === "FROZEN" ? "warning" : "default"} 
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton 
                          size="small" 
                          title={e.canRequestTutoring 
                            ? (t("employees.disableRequestTutoring") || "Disable request tutoring") 
                            : (t("employees.enableRequestTutoring") || "Enable request tutoring")} 
                          onClick={() => handleToggleCanRequestTutoring(e._id, e.canRequestTutoring)}
                          color={e.canRequestTutoring ? "primary" : "default"}
                        >
                          {e.canRequestTutoring ? <School /> : <SchoolOutlined />}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          title={e.accountStatus === "FROZEN" 
                            ? (t("employees.unfreeze") || "Unfreeze") 
                            : (t("employees.freeze") || "Freeze")} 
                          onClick={() => handleToggleFreeze(e._id, e.accountStatus)}
                        >
                          {e.accountStatus === "FROZEN" ? <LockOpen /> : <Block />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {invitations.length > 0 && (
            <Card>
              <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
                {t("employees.pendingInvitations") || "Pending invitations"}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("employees.tableEmail") || "Email"}</TableCell>
                    <TableCell>{t("employees.tableExpires") || "Expires"}</TableCell>
                    <TableCell align="right">{t("employees.tableActions") || "Actions"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((inv) => (
                    <TableRow key={inv._id}>
                      <TableCell>{inv.email}</TableCell>
                      <TableCell>{inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : "—"}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleResend(inv._id)}>
                          <Refresh />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)}>
        <DialogTitle>{t("employees.inviteByEmail") || "Invite by email"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t("common.email") || "Email"}
            type="email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={inviteForm.canRequestTutoring}
                onChange={(e) => setInviteForm({ ...inviteForm, canRequestTutoring: e.target.checked })}
              />
            }
            label={t("employees.canRequestTutoring") || "Can request tutoring"}
            sx={{ mt: 1, display: "block" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>{t("common.cancel") || "Cancel"}</Button>
          <Button variant="contained" onClick={handleInvite} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : (t("employees.sendInvitation") || "Send invitation")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>{t("employees.createWithUsername") || "Create account (no email)"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label={t("signup.firstName") || "First name"} value={createForm.firstName} onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} sx={{ mt: 1 }} />
          <TextField fullWidth label={t("signup.lastName") || "Last name"} value={createForm.lastName} onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} sx={{ mt: 1 }} />
          <TextField fullWidth label={t("common.username") || "Username"} value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} sx={{ mt: 1 }} />
          <TextField fullWidth label={t("common.password") || "Password"} type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} sx={{ mt: 1 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={createForm.canRequestTutoring}
                onChange={(e) => setCreateForm({ ...createForm, canRequestTutoring: e.target.checked })}
              />
            }
            label={t("employees.canRequestTutoring") || "Can request tutoring"}
            sx={{ mt: 1, display: "block" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t("common.cancel") || "Cancel"}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : (t("common.create") || "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Dialog 
        open={detailOpen} 
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedEmployee ? name(selectedEmployee.userId) : ""} {t("employees.details") || "Details"}
          </Typography>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : employeeDetails ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
                {t("employees.personalInformation") || "Personal Information"}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">{t("common.name") || "Name"}</Typography>
                  <Typography variant="body1">
                    {employeeDetails.student?.userId?.firstName} {employeeDetails.student?.userId?.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">{t("employees.tableEmailUsername") || "Email/Username"}</Typography>
                  <Typography variant="body1">
                    {employeeDetails.student?.userId?.email || employeeDetails.student?.userId?.username || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">{t("common.phoneNumber") || "Phone Number"}</Typography>
                  <Typography variant="body1">
                    {employeeDetails.student?.userId?.phoneNumber || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">{t("employees.tableCanRequestTutoring") || "Can request tutoring"}</Typography>
                  <Typography variant="body1">
                    {employeeDetails.student?.canRequestTutoring ? (t("common.yes") || "Yes") : (t("common.no") || "No")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">{t("employees.tableStatus") || "Status"}</Typography>
                  <Chip 
                    label={employeeDetails.student?.accountStatus === "FROZEN" 
                      ? (t("employees.statusFrozen") || "Frozen") 
                      : (t("employees.statusActive") || "Active")} 
                    size="small" 
                    color={employeeDetails.student?.accountStatus === "FROZEN" ? "warning" : "default"} 
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("employees.tutors") || "Tutors"} ({employeeDetails.tutors?.length || 0})
              </Typography>
              {employeeDetails.tutors && employeeDetails.tutors.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("common.name") || "Name"}</TableCell>
                      <TableCell>{t("employees.totalSessions") || "Total Sessions"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeDetails.tutors.map((tutorData) => (
                      <TableRow key={tutorData.tutor?._id}>
                        <TableCell>
                          {tutorData.tutor?.userId?.firstName} {tutorData.tutor?.userId?.lastName}
                        </TableCell>
                        <TableCell>{tutorData.totalSessions || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("employees.noTutors") || "No tutors assigned yet."}
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>{t("common.close") || "Close"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
